import {createSlice, type PayloadAction} from "@reduxjs/toolkit";
import type {
    AppliedPromoCode,
    Plan,
    PremiumPricingPanelState,
    YotpoReviewsMeta
} from "@Panels/premium-pricing-panel/premium-pricing-panel.types";
import {PlanCycle} from "@Panels/premium-pricing-panel/premium-pricing-panel.types";
import {getLocalCurrencyForCheckout, getPlansForPricingDialog, getPromoCodeDetails} from "@Panels/premium-pricing-panel/premium-pricing-panel-thunk";
import {isPromoApplicableToCycle, mapSubscriptionPlanToPlan} from "@Panels/premium-pricing-panel/premium-pricing-library";
import type {SubscriptionPlan} from "@Panels/change-plan-modal/change-plan-modal.types";
import type {SubscriptionCheckoutRequest} from '@Components/checkout-flow/checkout-flow.types';

const initialState: PremiumPricingPanelState = {
    plans: [],
    isWaitingForAJAXResponse: false,
    selectedCycle: PlanCycle.MONTHLY,
    yotpoData: undefined,
    appliedPromoCode: null,
    isPromoCodeLookupPending: false,
    checkoutRequest: null,
    localCheckoutCurrencyCode: null
};

const SELECTABLE_PLAN_CYCLES: readonly PlanCycle[] = [PlanCycle.MONTHLY, PlanCycle.YEARLY];

export const premiumPricingPanelSlice = createSlice({
    name: 'premiumPricingPanel',
    initialState,
    reducers: {
        updateSelectedCycle: (state, {payload}: PayloadAction<PlanCycle>) => {
            state.selectedCycle = payload;
        },
        setIsWaitingForAJAXResponse: (state, {payload}: PayloadAction<boolean>) => {
            state.isWaitingForAJAXResponse = payload;
        },
        updatePlans: (state, {payload}: PayloadAction<Plan[]>) => {
            state.plans = payload;
        },
        clearAppliedPromoCode: (state) => {
            state.appliedPromoCode = null;
            state.isPromoCodeLookupPending = false;
        },
        startSubscriptionCheckout: (state, {payload}: PayloadAction<SubscriptionCheckoutRequest>) => {
            state.checkoutRequest = payload;
            state.isWaitingForAJAXResponse = false;
        },
        clearSubscriptionCheckout: (state) => {
            state.checkoutRequest = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getPlansForPricingDialog.pending, (state) => {
            state.isWaitingForAJAXResponse = true;
        });
        builder.addCase(getPlansForPricingDialog.fulfilled, (state, {payload}: PayloadAction<{
            plans: SubscriptionPlan[],
            yotpoData: YotpoReviewsMeta
        }>) => {
            state.plans = Object.values(payload.plans).map((plan: SubscriptionPlan) => {
                return mapSubscriptionPlanToPlan(plan);
            });
            if (payload.yotpoData !== null) {
                state.yotpoData = payload.yotpoData;
            }
            state.isWaitingForAJAXResponse = false;
        });
        builder.addCase(getPlansForPricingDialog.rejected, (state) => {
            state.isWaitingForAJAXResponse = false;
        });
        builder.addCase(getLocalCurrencyForCheckout.fulfilled, (state, {payload}: PayloadAction<string | null>) => {
            state.localCheckoutCurrencyCode = payload;
        });
        builder.addCase(getPromoCodeDetails.pending, (state) => {
            state.isPromoCodeLookupPending = true;
        });
        builder.addCase(getPromoCodeDetails.fulfilled, (state, {payload}: PayloadAction<AppliedPromoCode | null>) => {
            state.appliedPromoCode = payload;
            state.isPromoCodeLookupPending = false;
            if (!payload || isPromoApplicableToCycle(payload, state.selectedCycle)) {
                return;
            }

            const coveredCycle = SELECTABLE_PLAN_CYCLES.find((cycle) => isPromoApplicableToCycle(payload, cycle));
            if (coveredCycle) {
                state.selectedCycle = coveredCycle;
            }
        });
        // An aborted lookup is not a finished one. StrictMode's double-invoked mount effect aborts the
        // first request while the second is still in flight, and clearing the flag there would uncover
        // the currency note for the frames before the promotion lands.
        builder.addCase(getPromoCodeDetails.rejected, (state, {meta}) => {
            state.appliedPromoCode = null;
            if (!meta.aborted) {
                state.isPromoCodeLookupPending = false;
            }
        });
    }
});

export const {updateSelectedCycle, setIsWaitingForAJAXResponse, updatePlans, clearAppliedPromoCode, startSubscriptionCheckout, clearSubscriptionCheckout} = premiumPricingPanelSlice.actions;
export const premiumPricingPanelReducer = premiumPricingPanelSlice.reducer;